export interface ContentRect {
  offsetX: number
  offsetY: number
  width: number
  height: number
}

/**
 * Rect occupied by the actual <video> pixels inside a wrapper that renders
 * the video with `object-fit: contain` (letterboxed). containerW/H are the
 * wrapper's CSS px; videoW/H are the intrinsic video px (videoWidth/Height).
 * Mirrors the math previously inlined in ScreenShareAnnotation.
 */
export function videoDisplayRect(
  containerW: number,
  containerH: number,
  videoW: number,
  videoH: number,
): ContentRect {
  if (containerW === 0 || containerH === 0) return { offsetX: 0, offsetY: 0, width: 0, height: 0 }
  if (videoW === 0 || videoH === 0) return { offsetX: 0, offsetY: 0, width: containerW, height: containerH }
  const containerAspect = containerW / containerH
  const videoAspect = videoW / videoH
  if (containerAspect > videoAspect) {
    const width = containerH * videoAspect
    return { offsetX: (containerW - width) / 2, offsetY: 0, width, height: containerH }
  }
  const height = containerW / videoAspect
  return { offsetX: 0, offsetY: (containerH - height) / 2, width: containerW, height }
}

/**
 * Convert a client-space pointer position to a normalized [0..1] point inside
 * the video content rect. `rect` is the wrapper's getBoundingClientRect().
 * Returns null if the content rect is degenerate.
 */
export function normalizePoint(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number },
  vr: ContentRect,
): { x: number; y: number } | null {
  if (vr.width === 0 || vr.height === 0) return null
  const wrapperX = clientX - rect.left
  const wrapperY = clientY - rect.top
  return { x: (wrapperX - vr.offsetX) / vr.width, y: (wrapperY - vr.offsetY) / vr.height }
}
